/**
 * Leviathan Web - Driver Terminal Credentials Component
 * Quasar Prime LLC
 *
 * Table and modal CRUD for driver terminal credentials (card numbers, PINs).
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

// =====================================================
// TYPES
// =====================================================

interface Terminal {
  id: string;
  name: string;
  terminalCode: string | null;
  city: string | null;
  state: string | null;
}

interface TerminalCredential {
  id: string;
  driverId: string;
  terminalId: string;
  cardNumber: string | null;
  issueDate: string | null;
  expirationDate: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  terminal: Terminal;
}

interface CredentialFormData {
  terminalId: string;
  cardNumber: string;
  pin: string;
  issueDate: string;
  expirationDate: string;
  isActive: boolean;
  notes: string;
}

interface DriverTerminalCredentialsProps {
  driverId: string;
}

// =====================================================
// HELPERS
// =====================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateForInput(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0] ?? '';
}

type ExpirationLevel = 'expired' | 'warning' | 'ok' | null;

function getExpirationLevel(dateStr: string | null): ExpirationLevel {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  if (date < now) return 'expired';
  if (date <= thirtyDays) return 'warning';
  return 'ok';
}

function getExpirationBadge(dateStr: string | null): { text: string; className: string } {
  const level = getExpirationLevel(dateStr);
  const formatted = formatDate(dateStr);
  switch (level) {
    case 'expired':
      return {
        text: `${formatted} EXPIRED`,
        className: 'text-error-red bg-error-red/10 border border-error-red/30',
      };
    case 'warning':
      return {
        text: `${formatted} !`,
        className: 'text-warning-amber bg-warning-amber/10 border border-warning-amber/30',
      };
    case 'ok':
      return {
        text: formatted,
        className: 'text-success-green bg-success-green/10 border border-success-green/30',
      };
    default:
      return {
        text: '-',
        className: 'text-text-tertiary',
      };
  }
}

const EMPTY_FORM: CredentialFormData = {
  terminalId: '',
  cardNumber: '',
  pin: '',
  issueDate: '',
  expirationDate: '',
  isActive: true,
  notes: '',
};

// =====================================================
// COMPONENT
// =====================================================

const DriverTerminalCredentials = ({ driverId }: DriverTerminalCredentialsProps) => {
  const { accessToken } = useAuth();

  const [credentials, setCredentials] = useState<TerminalCredential[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CredentialFormData>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // =====================================================
  // DATA FETCHING
  // =====================================================

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/terminal-credentials`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCredentials(data.credentials);
      } else {
        const msg = await parseApiError(response);
        setError(msg);
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
      setError('Failed to load terminal credentials');
    }
  }, [driverId, accessToken]);

  const fetchTerminals = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/terminals`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTerminals(data.terminals || []);
      }
    } catch (err) {
      console.error('Failed to fetch terminals:', err);
    }
  }, [accessToken]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([fetchCredentials(), fetchTerminals()]);
      setIsLoading(false);
    };
    load();
  }, [fetchCredentials, fetchTerminals]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const openAddModal = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (cred: TerminalCredential) => {
    setEditingId(cred.id);
    setFormData({
      terminalId: cred.terminalId,
      cardNumber: '', // Don't prefill masked value - user re-enters if changing
      pin: '',
      issueDate: formatDateForInput(cred.issueDate),
      expirationDate: formatDateForInput(cred.expirationDate),
      isActive: cred.isActive,
      notes: cred.notes || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
  };

  const handleSave = async () => {
    setFormError(null);

    if (!formData.terminalId) {
      setFormError('Please select a terminal');
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, unknown> = {
        terminalId: formData.terminalId,
        isActive: formData.isActive,
      };

      // Only include non-empty optional fields
      if (formData.cardNumber) body.cardNumber = formData.cardNumber;
      if (formData.pin) body.pin = formData.pin;
      if (formData.issueDate) body.issueDate = formData.issueDate;
      if (formData.expirationDate) body.expirationDate = formData.expirationDate;
      if (formData.notes) body.notes = formData.notes;

      const url = editingId
        ? `${API_URL}/api/drivers/${driverId}/terminal-credentials/${editingId}`
        : `${API_URL}/api/drivers/${driverId}/terminal-credentials`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        closeModal();
        await fetchCredentials();
      } else {
        const msg = await parseApiError(response);
        setFormError(msg);
      }
    } catch (err) {
      console.error('Failed to save credential:', err);
      setFormError('Failed to save credential');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/drivers/${driverId}/terminal-credentials/${deleteId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.ok) {
        setDeleteId(null);
        await fetchCredentials();
      } else {
        const msg = await parseApiError(response);
        setError(msg);
      }
    } catch (err) {
      console.error('Failed to delete credential:', err);
      setError('Failed to delete credential');
    } finally {
      setIsDeleting(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading terminal credentials...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            Terminal Credentials
          </h3>
          <span className="text-text-tertiary font-mono text-xs ml-2">
            [{credentials.length}]
          </span>
        </div>
        <button
          onClick={openAddModal}
          className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-4 transition-all uppercase tracking-wider text-sm"
          style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.3)' }}
        >
          + Add Credential
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/30 p-3 font-mono text-sm text-error-red">
          {error}
          <button onClick={() => setError(null)} className="ml-3 underline hover:no-underline">
            dismiss
          </button>
        </div>
      )}

      {/* Table */}
      {credentials.length === 0 ? (
        <div className="bg-panel-dark border border-panel-border p-8 text-center">
          <div className="text-text-tertiary font-mono text-sm">
            &gt; No terminal credentials on file
          </div>
          <div className="text-text-tertiary font-mono text-xs mt-2">
            Add a credential to enable terminal access for this driver
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* Corner brackets */}
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-panel-border bg-background/50">
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Terminal
                  </th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Card #
                  </th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Expiration
                  </th>
                  <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {credentials.map((cred) => {
                  const expBadge = getExpirationBadge(cred.expirationDate);
                  return (
                    <tr
                      key={cred.id}
                      className="border-b border-panel-border/50 hover:bg-steel/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-mono text-sm text-text-primary">
                          {cred.terminal.name}
                        </div>
                        <div className="font-mono text-xs text-text-tertiary">
                          {cred.terminal.terminalCode && `${cred.terminal.terminalCode} | `}
                          {cred.terminal.city}{cred.terminal.state ? `, ${cred.terminal.state}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-text-primary tracking-wider">
                          {cred.cardNumber || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block font-mono text-xs px-2 py-1 rounded ${expBadge.className}`}
                        >
                          {expBadge.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded ${
                            cred.isActive
                              ? 'text-success-green bg-success-green/10'
                              : 'text-text-tertiary bg-text-tertiary/10'
                          }`}
                        >
                          {cred.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button
                          onClick={() => openEditModal(cred)}
                          className="text-steel hover:text-amber font-mono text-xs uppercase tracking-wider transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(cred.id)}
                          className="text-text-tertiary hover:text-error-red font-mono text-xs uppercase tracking-wider transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-lg w-full mx-4">
            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6">
              <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                {editingId ? 'Edit' : 'Add'} Terminal Credential
              </h3>

              {formError && (
                <div className="bg-error-red/10 border border-error-red/30 p-3 mb-4 font-mono text-sm text-error-red">
                  {formError}
                </div>
              )}

              <div className="space-y-4">
                {/* Terminal selector */}
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Terminal *
                  </label>
                  <select
                    value={formData.terminalId}
                    onChange={(e) => setFormData({ ...formData, terminalId: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value="">-- Select Terminal --</option>
                    {terminals.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.terminalCode ? ` (${t.terminalCode})` : ''}
                        {t.city ? ` - ${t.city}` : ''}
                        {t.state ? `, ${t.state}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Card Number */}
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Card Number
                    {editingId && (
                      <span className="text-text-tertiary ml-2">(leave blank to keep current)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={formData.cardNumber}
                    onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                    className="form-input w-full"
                    placeholder="Enter card number"
                    autoComplete="off"
                  />
                </div>

                {/* PIN */}
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    PIN
                    {editingId && (
                      <span className="text-text-tertiary ml-2">(leave blank to keep current)</span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                    className="form-input w-full"
                    placeholder="Enter PIN"
                    autoComplete="new-password"
                  />
                </div>

                {/* Dates row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Issue Date
                    </label>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={formData.expirationDate}
                      onChange={(e) =>
                        setFormData({ ...formData, expirationDate: e.target.value })
                      }
                      className="form-input w-full"
                    />
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between py-2">
                  <label className="text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Active
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      formData.isActive ? 'bg-success-green' : 'bg-panel-border'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.isActive ? 'translate-x-6' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input w-full h-20 resize-none"
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-6">
              <h3 className="text-xl font-display text-error-red uppercase tracking-wide mb-4">
                Delete Credential
              </h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete this terminal credential? This action cannot be
                undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-error-red hover:bg-error-red/90 text-white font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverTerminalCredentials;
